import type { Point, ConnectorStyle, ArrowHead } from '../state/types';
import type { Shape } from '../shapes/Shape';
import type { DiagramManagerSelectionContext } from '../state/DiagramContext';
import { Connector } from '../shapes/Connector';
import { filterBySelection } from '../state/diagramSelection';
import { CONNECTOR } from "../../../config/constants";

interface DiagramConnectorContext extends DiagramManagerSelectionContext {
  ctx: CanvasRenderingContext2D;
  getGridSize: () => number;
  getPanScale: () => { panX: number; panY: number; scale: number };
  getCurrentConnectorStyle: () => Partial<ConnectorStyle>;
  snapToGrid: (point: Point) => Point;
  notifySelectionChange: () => void;
  fadeOutPlaceholder: () => void;
}

export class DiagramConnectorManager {
  private readonly context: DiagramConnectorContext;
  private connectorStart: Point | null = null;
  private connectorStartConnection: { shapeId: string; connectionPointId: string } | null = null;

  constructor(context: DiagramConnectorContext) {
    this.context = context;
  }

  hasActiveConnector(): boolean {
    return this.connectorStart !== null;
  }

  startConnector(point: Point): void {
    const connectionInfo = this.findNearestConnectionPoint(point);
    if (connectionInfo) {
      this.connectorStart = { ...connectionInfo.position };
      this.connectorStartConnection = {
        shapeId: connectionInfo.shapeId,
        connectionPointId: connectionInfo.connectionPointId,
      };
    } else {
      this.connectorStart = this.context.snapToGrid(point);
      this.connectorStartConnection = null;
    }
  }

  finishConnector(point: Point): void {
    if (!this.connectorStart) return;

    const endConnectionInfo = this.findNearestConnectionPoint(point);
    const endPoint = endConnectionInfo ? { ...endConnectionInfo.position } : this.context.snapToGrid(point);

    const minDistance = this.context.getGridSize() * 2;
    const distance = Math.hypot(endPoint.x - this.connectorStart.x, endPoint.y - this.connectorStart.y);
    if (distance < minDistance) {
      this.connectorStart = null;
      this.connectorStartConnection = null;
      this.context.render();
      return;
    }

    const connector = new Connector(this.connectorStart, endPoint, this.context.getCurrentConnectorStyle());
    if (this.connectorStartConnection) {
      connector.startShapeId = this.connectorStartConnection.shapeId;
      connector.startConnectionPointId = this.connectorStartConnection.connectionPointId;
    }
    if (endConnectionInfo) {
      connector.endShapeId = endConnectionInfo.shapeId;
      connector.endConnectionPointId = endConnectionInfo.connectionPointId;
    }

    this.context.store.connectors.push(connector);
    this.context.fadeOutPlaceholder();
    this.context.clearSelection();
    this.context.selectElement(connector);
    this.connectorStart = null;
    this.connectorStartConnection = null;
    this.context.notifyChange();
    this.context.render();
  }

  renderConnectorPreview(point: Point): void {
    this.context.render();

    if (!this.connectorStart) return;

    const previewColor = this.context.getInverted() ? '#60a5fa' : '#2563eb';
    const { panX, panY, scale } = this.context.getPanScale();

    this.context.ctx.save();
    this.context.ctx.translate(panX, panY);
    this.context.ctx.scale(scale, scale);

    this.context.ctx.strokeStyle = previewColor;
    this.context.ctx.lineWidth = 2 / scale;
    this.context.ctx.setLineDash([4 / scale, 4 / scale]);

    this.context.ctx.beginPath();
    this.context.ctx.moveTo(this.connectorStart.x, this.connectorStart.y);
    this.context.ctx.lineTo(point.x, point.y);
    this.context.ctx.stroke();

    const angle = Math.atan2(point.y - this.connectorStart.y, point.x - this.connectorStart.x);
    this.context.ctx.setLineDash([]);
    this.context.ctx.fillStyle = previewColor;
    this.context.ctx.translate(point.x, point.y);
    this.context.ctx.rotate(angle);
    const arrowSize = CONNECTOR.arrowSize / scale;
    this.context.ctx.beginPath();
    this.context.ctx.moveTo(0, 0);
    this.context.ctx.lineTo(-arrowSize, -arrowSize / 2);
    this.context.ctx.lineTo(-arrowSize, arrowSize / 2);
    this.context.ctx.closePath();
    this.context.ctx.fill();

    this.context.ctx.restore();

    this.context.ctx.save();
    this.context.ctx.translate(panX, panY);
    this.context.ctx.scale(scale, scale);

    for (const shape of this.context.store.shapes) {
      for (const cp of shape.getConnectionPoints()) {
        const dist = Math.hypot(point.x - cp.position.x, point.y - cp.position.y);
        if (dist < 30) {
          this.context.ctx.fillStyle = dist < 15
            ? previewColor
            : (this.context.getInverted() ? 'rgba(96, 165, 250, 0.3)' : 'rgba(37, 99, 235, 0.3)');
          this.context.ctx.beginPath();
          this.context.ctx.arc(cp.position.x, cp.position.y, 6 / scale, 0, Math.PI * 2);
          this.context.ctx.fill();
        }
      }
    }

    this.context.ctx.restore();
  }

  findNearestConnectionPoint(point: Point, snapDistance = this.context.getGridSize() * 2): { position: Point; shapeId: string; connectionPointId: string } | null {
    let nearest: { position: Point; shapeId: string; connectionPointId: string } | null = null;
    let nearestDist = snapDistance;

    for (const shape of this.context.store.shapes) {
      for (const cp of shape.getConnectionPoints()) {
        const dist = Math.hypot(point.x - cp.position.x, point.y - cp.position.y);
        if (dist < nearestDist) {
          nearestDist = dist;
          nearest = {
            position: { ...cp.position },
            shapeId: shape.id,
            connectionPointId: cp.id,
          };
        }
      }
    }

    return nearest;
  }

  snapAndUpdateConnectorEndpoints(connector: Connector): void {
    const startConnectionInfo = this.findNearestConnectionPoint(connector.startPoint);
    if (startConnectionInfo) {
      connector.startPoint = { ...startConnectionInfo.position };
      connector.startShapeId = startConnectionInfo.shapeId;
      connector.startConnectionPointId = startConnectionInfo.connectionPointId;
    } else {
      connector.startShapeId = null;
      connector.startConnectionPointId = null;
      connector.startPoint = this.context.snapToGrid(connector.startPoint);
    }

    const endConnectionInfo = this.findNearestConnectionPoint(connector.endPoint);
    if (endConnectionInfo) {
      connector.endPoint = { ...endConnectionInfo.position };
      connector.endShapeId = endConnectionInfo.shapeId;
      connector.endConnectionPointId = endConnectionInfo.connectionPointId;
    } else {
      connector.endShapeId = null;
      connector.endConnectionPointId = null;
      connector.endPoint = this.context.snapToGrid(connector.endPoint);
    }
  }

  updateConnectedConnectors(shape: Shape, skipConnectorIds?: Set<string>): void {
    const connectionPoints = shape.getConnectionPoints();

    for (const connector of this.context.store.connectors) {
      if (skipConnectorIds?.has(connector.id)) {
        continue;
      }
      if (connector.startShapeId === shape.id) {
        const cp = connectionPoints.find((p) => p.id === connector.startConnectionPointId);
        if (cp) {
          connector.startPoint = { ...cp.position };
        }
      }

      if (connector.endShapeId === shape.id) {
        const cp = connectionPoints.find((p) => p.id === connector.endConnectionPointId);
        if (cp) {
          connector.endPoint = { ...cp.position };
        }
      }
    }
  }

  tryAutoConnectShape(shape: Shape): void {
    const connectionThreshold = this.context.getGridSize() * 2;
    const connectionPoints = shape.getConnectionPoints();

    for (const connector of this.context.store.connectors) {
      if (connector.startShapeId === shape.id && connector.endShapeId === shape.id) {
        continue;
      }

      if (!connector.startShapeId) {
        for (const cp of connectionPoints) {
          const dist = Math.hypot(
            connector.startPoint.x - cp.position.x,
            connector.startPoint.y - cp.position.y
          );
          if (dist <= connectionThreshold) {
            connector.startShapeId = shape.id;
            connector.startConnectionPointId = cp.id;
            connector.startPoint = { ...cp.position };
            break;
          }
        }
      }

      if (!connector.endShapeId) {
        for (const cp of connectionPoints) {
          const dist = Math.hypot(
            connector.endPoint.x - cp.position.x,
            connector.endPoint.y - cp.position.y
          );
          if (dist <= connectionThreshold) {
            connector.endShapeId = shape.id;
            connector.endConnectionPointId = cp.id;
            connector.endPoint = { ...cp.position };
            break;
          }
        }
      }
    }
  }

  updateSelectedConnectorStyle(style: { startArrow?: ArrowHead; endArrow?: ArrowHead; lineStyle?: 'solid' | 'dashed' | 'dotted'; strokeWidth?: number }): void {
    const selectedConnectors = filterBySelection(
      this.context.store.connectors,
      this.context.store.selectedElements
    );
    for (const connector of selectedConnectors) {
      if (style.startArrow !== undefined) {
        connector.style.startArrow = style.startArrow;
      }
      if (style.endArrow !== undefined) {
        connector.style.endArrow = style.endArrow;
      }
      if (style.lineStyle !== undefined) {
        connector.style.lineStyle = style.lineStyle;
      }
      if (style.strokeWidth !== undefined) {
        connector.style.strokeWidth = style.strokeWidth;
      }
    }
    this.context.notifyChange();
    this.context.render();
  }

  toggleSelectedConnectorArc(): void {
    const selectedConnectors = filterBySelection(
      this.context.store.connectors,
      this.context.store.selectedElements
    );
    for (const connector of selectedConnectors) {
      if (connector.arcPoint) {
        connector.arcPoint = null;
      } else {
        const midX = (connector.startPoint.x + connector.endPoint.x) / 2;
        const midY = (connector.startPoint.y + connector.endPoint.y) / 2;

        const dx = connector.endPoint.x - connector.startPoint.x;
        const dy = connector.endPoint.y - connector.startPoint.y;
        const length = Math.hypot(dx, dy);
        const offset = CONNECTOR.placementOffset;

        if (length === 0) {
          connector.arcPoint = { x: midX, y: midY - offset };
        } else {
          const perpX = (-dy / length) * offset;
          const perpY = (dx / length) * offset;
          connector.arcPoint = {
            x: midX + perpX,
            y: midY + perpY,
          };
        }
      }
      this.context.notifySelectionChange();
    }
    this.context.notifyChange();
    this.context.render();
  }

  syncConnectorEndpoints(): void {
    for (const connector of this.context.store.connectors) {
      if (connector.startShapeId && connector.startConnectionPointId) {
        const shape = this.context.store.shapes.find((s) => s.id === connector.startShapeId);
        if (shape) {
          const direction = this.extractConnectionDirection(connector.startConnectionPointId);
          const connectionPoints = shape.getConnectionPoints();
          const cp = connectionPoints.find((p) => p.id.endsWith(`-${direction}`));
          if (cp) {
            connector.startPoint = { ...cp.position };
            connector.startConnectionPointId = cp.id;
          }
        }
      }

      if (connector.endShapeId && connector.endConnectionPointId) {
        const shape = this.context.store.shapes.find((s) => s.id === connector.endShapeId);
        if (shape) {
          const direction = this.extractConnectionDirection(connector.endConnectionPointId);
          const connectionPoints = shape.getConnectionPoints();
          const cp = connectionPoints.find((p) => p.id.endsWith(`-${direction}`));
          if (cp) {
            connector.endPoint = { ...cp.position };
            connector.endConnectionPointId = cp.id;
          }
        }
      }
    }
  }

  private extractConnectionDirection(connectionPointId: string): string {
    const parts = connectionPointId.split('-');
    if (parts.length >= 2) {
      return parts.slice(1).join('-');
    }
    return parts[0] ?? '';
  }
}
